import React from 'react';
import PropTypes from 'prop-types';
import CtaPropTypes from '../../prop-types/cta.pim';

import * as Styled from './CTABlock.styled';

/**
 * A block that renders 1 through many CTAs
 *
 * CTAs can also render inside an overflow container
 *
 * CTAS are styled per the content system
 */
const CTABlock = ({
  ctas,
  color,
  defaultVariant,
  withOverflow,
  overflowOffset,
  ...props
}) => {
  const Wrapper = withOverflow ? Styled.Overflow : React.Fragment;

  return (
    <Styled.CTABlock {...props}>
      <Wrapper {...(withOverflow ? { $overflowOffset: overflowOffset } : {})}>
        {ctas.map(({ url, text, onClick, variant, color: ctaColor }, i) => (
          <Styled.CTA
            $backgroundColor={ctaColor || color}
            as={onClick && !url ? 'button' : 'a'}
            key={i}
            href={url}
            size="medium"
            variant={variant || defaultVariant}
            onClick={
              typeof onClick === 'function'
                ? (e) => {
                    e.preventDefault();
                    onClick();
                  }
                : null
            }
          >
            {text}
          </Styled.CTA>
        ))}
      </Wrapper>
    </Styled.CTABlock>
  );
};

CTABlock.propTypes = {
  /**
   * An array of CTAs provided from a parent
   */
  ctas: PropTypes.arrayOf(PropTypes.shape(CtaPropTypes)),
  /**
   * The color that will determine the button's background color (black or white) and text color (white or black)
   */
  color: PropTypes.string,
  /**
   * A variant type as defined in src/components/Button/Button.js
   *
   * To use this prop, do not pass a value for color.
   *
   * The color prop will otherwise override variant.
   */
  defaultVariant: PropTypes.string,
  /**
   * A boolean to toggle an overflow container
   */
  withOverflow: PropTypes.bool,
  /**
   * A string, either in px or percent, that modifies the left and right padding of `Styled.Overflow`
   */
  overflowOffset: PropTypes.string
};

CTABlock.defaultProps = {
  ctas: [],
  color: null,
  defaultVariant: 'default',
  withOverflow: false,
  overflowOffset: undefined
};

export default CTABlock;
